import { useState, useMemo } from 'react'
import { PasteLinkProps } from './Types'
import Styles from './MediaFromLinkDrawer.module.scss'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Button } from 'blaze-ui/Button'
import { Image } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { capitalize } from 'lodash'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { checkUrl, fetchFile, FileError, validateMimeType } from 'utils/fileDownloadUtils'

export const PasteLink = ({ accept, url, setUrl, onFetch }: PasteLinkProps) => {
  const isUrlValid = useMemo(() => checkUrl(url), [url])
  const [fileError, setFileError] = useState<FileError>()
  const [fetchingFile, setFetchingFile] = useState(false)

  const mediaType = useMemo(() => {
    if (accept.includes('image') && accept.includes('video')) {
      return 'image/video'
    } else if (accept.includes('image')) {
      return 'image'
    } else if (accept.includes('video')) {
      return 'video'
    }

    return 'media'
  }, [accept])

  const errorText = useMemo(() => {
    if (fileError === 'invalid') {
      return `This is an unsupported ${mediaType} link`
    } else if (fileError === 'error') {
      return `We're having trouble loading this ${mediaType} link`
    }
  }, [mediaType, fileError])

  const handleSubmit = async () => {
    setFetchingFile(true)
    const error = await validateMimeType(url, accept)
    if (!!error) {
      setFileError(error)
      setFetchingFile(false)
      return
    }

    const dismissToast = showToastMessage('Downloading file...', 'loading')
    try {
      const file = await fetchFile({ url })
      onFetch(file)
    } catch (error) {
      setFileError('error')
    } finally {
      setFetchingFile(false)
      dismissToast()
    }
  }

  return (
    <>
      <div className={Styles.previewInfo}>
        <Image size={77} />
        <Text variant="secondary">{capitalize(mediaType)} preview will appear here</Text>
      </div>
      <div className={Styles.actions}>
        <div className={Styles.inputContainer}>
          <div className={Styles.errorText}>
            {!!errorText?.length && (
              <Text variant="metadata" color="var(--negative-50)">
                {errorText}
              </Text>
            )}
          </div>
          <Input
            name="Link Input"
            placeholder={`Paste a valid ${mediaType} link`}
            size="lg"
            isValid={url.length === 0 || (isUrlValid && fileError == null)}
            value={url}
            onChange={(e) => {
              setFileError(undefined)
              setUrl(e.target.value)
            }}
            autoFocus
            autoComplete="off"
          />
        </div>
        <Button
          aria-label="Confirm"
          variant="primary"
          size="lg"
          isDisabled={!isUrlValid || fetchingFile}
          onPress={handleSubmit}
          fullWidth
        >
          Confirm
        </Button>
      </div>
    </>
  )
}
